import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { Music, Wrench, Building2, X, Shield, Eye, EyeOff } from 'lucide-react';
import Wordmark from '../components/Wordmark';

export default function SignupPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [profileType, setProfileType] = useState('MUSICIAN');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    const missing = [];
    if (!email) missing.push('Email');
    if (!password) missing.push('Password');
    if (password && password.length < 10) missing.push('Password (min 10 characters)');
    if (!profileType) missing.push('Profile type');

    if (missing.length > 0) {
      toast.error(`Missing: ${missing.join(', ')}`);
      return;
    }

    setLoading(true);

    try {
      await signup(email, password, profileType);
      toast.success('Account created! Please complete your profile.');
      const onboardingPath = { MUSICIAN: '/onboarding/musician', TECH: '/onboarding/tech', VENUE: '/onboarding/venue' }[profileType] || '/';
      navigate(onboardingPath);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg || JSON.stringify(d)).join(', '));
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to create account');
      }
      setLoading(false);
    }
  };

  const profileOptions = [
    { value: 'MUSICIAN', label: 'Musician', description: 'Summon music from my hands', icon: Music },
    { value: 'TECH', label: 'Technical Crew', description: 'Pilot audio / lighting / video equipment', icon: Wrench },
    { value: 'VENUE', label: 'Venue / Organizer', description: 'Facilitate live music events', icon: Building2 },
  ];

  const handleClose = () => {
    // navigate('/') instead of navigate(-1): when /signup is opened directly
    // (fresh tab, external link) there is no history to go back to.
    navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)', fontFamily: 'var(--font-body)' }}>
      <div className="w-full max-w-md rounded-[var(--s8-radius-lg)] overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--b1-solid)' }}>
        {/* Header */}
        <div className="p-8 pb-6 relative text-center" style={{ background: 'var(--bg-card-inner)' }}>
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1.5 rounded-[var(--s8-radius-md)] transition-colors"
            style={{ color: 'var(--t3)' }}
            aria-label="Close"
            data-testid="signup-close-btn"
          >
            <X className="h-5 w-5" />
          </button>
          <Wordmark className="h-[40px] mx-auto" />
          <p className="mt-2 text-sm" style={{ color: 'var(--t2)' }}>
            Create your account to get started
          </p>
        </div>

        {/* Divider */}
        <div style={{ borderTop: '1px solid var(--b1-solid)' }} />

        {/* Form */}
        <div className="p-6 pt-5">
          <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider" style={{ fontFamily: 'var(--font-ui)', color: 'var(--t3)' }}>Email</Label>
              <Input
                id="email"
                name="signup-email"
                type="email"
                value={email}
                defaultValue=""
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="new-email"
                className="h-12 rounded-[var(--s8-radius-md)]"
                data-testid="signup-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider" style={{ fontFamily: 'var(--font-ui)', color: 'var(--t3)' }}>Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="signup-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  defaultValue=""
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 10 characters"
                  required
                  minLength={10}
                  autoComplete="new-password"
                  className="h-12 rounded-[var(--s8-radius-md)] pr-12"
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-0 top-0 h-12 w-12 flex items-center justify-center"
                  style={{ color: 'var(--t3)' }}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="signup-toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs" style={{ color: 'var(--t3)' }}>Minimum 10 characters</p>
            </div>

            <div className="space-y-3">
              <Label className="text-xs uppercase tracking-wider" style={{ fontFamily: 'var(--font-ui)', color: 'var(--t3)' }}>I have the ability to:</Label>
              <RadioGroup value={profileType} onValueChange={setProfileType} className="space-y-2">
                {profileOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = profileType === option.value;
                  return (
                    <label
                      key={option.value}
                      className="flex items-center gap-4 p-4 rounded-[var(--s8-radius-md)] cursor-pointer transition-all"
                      style={{
                        border: `1px solid ${isSelected ? 'var(--amber)' : 'var(--b1-solid)'}`,
                        background: isSelected ? 'rgba(200,146,62,0.08)' : 'var(--bg-card-inner)',
                      }}
                      data-testid={`profile-type-${option.value.toLowerCase()}`}
                    >
                      <RadioGroupItem value={option.value} id={option.value} />
                      <Icon className="h-5 w-5" style={{ color: isSelected ? 'var(--amber)' : 'var(--t2)' }} />
                      <div className="flex-1">
                        <div className="text-sm font-medium" style={{ color: isSelected ? 'var(--t0)' : 'var(--t1)' }}>{option.label}</div>
                        <div
                          className="text-xs"
                          style={{ color: 'var(--t3)' }}
                          data-testid={`role-description-${option.value.toLowerCase()}`}
                        >
                          {option.description}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </RadioGroup>
            </div>

            <Button
              type="submit"
              className="w-full mt-6 h-12 text-sm font-semibold uppercase tracking-wider rounded-[var(--s8-radius-pill)]"
              disabled={loading}
              style={{ fontFamily: 'var(--font-ui)', background: 'var(--amber)', color: 'var(--bg)' }}
              data-testid="signup-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span style={{ color: 'var(--t3)' }}>Already have an account? </span>
            <Link to="/login" className="font-medium" style={{ color: 'var(--amber)' }} data-testid="login-link">
              Sign in
            </Link>
          </div>

          <div className="flex items-center justify-center gap-2 mt-5 pt-4 text-xs" style={{ color: 'var(--t3)', borderTop: '1px solid var(--b1-solid)' }}>
            <Shield className="h-3.5 w-3.5" />
            <span>Secure signup protected by STAGE8</span>
          </div>
        </div>
      </div>
    </div>
  );
}
